import API from '../api/APIClient';
import { addDays } from '../utils/date';
import { numberToGimatria } from '../utils/gematria';

const CLOSED_NOTICE_DURATION_MS = 3 * 60 * 60 * 1000;

export function buildShabbatPlanningQuery(dateKey, now) {
  const through = `${addDays(dateKey, 8)}T00:00:00Z`;
  return '/crfd1_calendarevents?$select=crfd1_calendareventid,crfd1_name,crfd1_shabbattype,crfd1_start,crfd1_end'
    + `&$filter=statecode eq 0 and crfd1_category eq 648970000 and crfd1_lifecycle eq 648970001 and (crfd1_shabbattype eq 648970001 or crfd1_shabbattype eq 648970002) and crfd1_end gt ${new Date(now).toISOString()} and crfd1_start lt ${through}`
    + '&$orderby=crfd1_start asc&$top=1';
}

export async function getShabbatPlanning(dateKey, now, { signal } = {}) {
  const read = query => API.select(encodeURIComponent(query), 'list', { signal, timeoutMs: 8000 });
  const events = await read(buildShabbatPlanningQuery(dateKey, now));
  const event = events?.[0];
  if (!event) return [];
  const campaigns = await read(`/crfd1_registrationcampaigns?$select=crfd1_registrationcampaignid,crfd1_closeson&$filter=statecode eq 0 and crfd1_behavior eq 648970001 and (crfd1_status eq 648970001 or crfd1_status eq 648970002) and _crfd1_event_value eq ${event.crfd1_calendareventid}&$top=2`);
  if (campaigns.length !== 1) return [{ ...event, registrationUnavailable: campaigns.length > 1 }];
  const campaign = campaigns[0];
  const closesAt = Date.parse(campaign.crfd1_closeson);
  if (Number.isFinite(closesAt) && now >= closesAt + CLOSED_NOTICE_DURATION_MS) return [];
  const [counts, breakdown] = await Promise.all([
    read(`/crfd1_registrationresponses?fetchXml=${encodeURIComponent(buildShabbatCountFetch(campaign.crfd1_registrationcampaignid))}`),
    Promise.all([
      read(`/crfd1_registrationresponses?fetchXml=${encodeURIComponent(buildShabbatClassCountFetch(campaign.crfd1_registrationcampaignid))}`),
      read('/crfd1_classes?$select=crfd1_classid,crfd1_year&$orderby=crfd1_year desc')
    ]).catch(() => null)
  ]);
  const count = counts?.[0];
  if (!count || !Number.isFinite(Number(count.attendees))) throw new Error('Shabbat attendance count is unavailable.');
  const attendees = Number(count.attendees), guests = Number(count.guests) || 0;
  return [{ ...event, closesOn: campaign.crfd1_closeson, attendees, guests,
    classCounts: breakdown ? buildShabbatClassCounts(breakdown[0], breakdown[1], attendees, guests) : []
  }];
}

export function buildShabbatCountFetch(campaignId) {
  if (!/^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(campaignId)) throw new Error('Invalid Shabbat registration ID.');
  return `<fetch aggregate="true"><entity name="crfd1_registrationresponse"><attribute name="crfd1_registrationresponseid" alias="attendees" aggregate="count" /><attribute name="crfd1_guestcount" alias="guests" aggregate="sum" /><filter><condition attribute="statecode" operator="eq" value="0" /><condition attribute="crfd1_status" operator="eq" value="648970000" /><condition attribute="crfd1_campaign" operator="eq" value="${campaignId}" /></filter><link-entity name="crfd1_registrationoption" from="crfd1_registrationoptionid" to="crfd1_selectedoption" link-type="inner"><filter><condition attribute="crfd1_code" operator="eq" value="stay" /></filter></link-entity></entity></fetch>`;
}

export function buildShabbatPlanningMessage(events, now = Date.now()) {
  const event = events?.[0];
  const kind = event?.crfd1_shabbattype === 648970001 ? 'שבת ישיבה'
    : event?.crfd1_shabbattype === 648970002 ? 'שבת חופשית' : '';
  if (!kind) return null;
  const closesAt = Date.parse(event.closesOn);
  const expiresAt = Number.isFinite(closesAt) ? closesAt + CLOSED_NOTICE_DURATION_MS : undefined;
  if (expiresAt !== undefined && now >= expiresAt) return null;
  let registrationDeadline = '';
  if (event.closesOn && Number.isFinite(Date.parse(event.closesOn))) {
    const date = new Intl.DateTimeFormat('he-IL', { timeZone: 'Asia/Jerusalem', weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(event.closesOn));
    const value = type => date.find(part => part.type === type)?.value;
    registrationDeadline = Date.parse(event.closesOn) <= now
      ? 'ההרשמה נסגרה'
      : `ההרשמה עד ${value('weekday')} ב-${value('hour')}:${value('minute')}`;
  }
  return {
    type: 'shabbat-planning',
    eventId: event.crfd1_calendareventid,
    crfd1_title: event.crfd1_name || 'השבת הקרובה',
    crfd1_duration: 7,
    expiresAt,
    crfd1_priority: 2,
    shabbatKind: kind,
    registrationDeadline,
    attendanceText: Number.isFinite(event.attendees)
      ? `${event.attendees + (event.guests || 0)} נרשמים`
      : '',
    classCounts: Number.isFinite(event.attendees) && Array.isArray(event.classCounts) ? event.classCounts : []
  };
}

export function buildShabbatClassCountFetch(campaignId) {
  return buildShabbatCountFetch(campaignId).replace('</entity></fetch>',
    '<link-entity name="crfd1_student" from="crfd1_systemuser" to="crfd1_user" link-type="outer"><attribute name="crfd1_class" alias="classId" groupby="true" /></link-entity></entity></fetch>');
}

export function buildShabbatClassCounts(groups, classes, attendees, guests) {
  if (!Array.isArray(groups) || !Array.isArray(classes) || !classes.length) return [];
  const ranked = [...classes].sort((a, b) => b.crfd1_year - a.crfd1_year);
  const ranks = new Map(ranked.map((row, index) => [String(row.crfd1_classid).toLowerCase(), index + 1]));
  const totals = new Map();
  let countedAttendees = 0, countedGuests = 0;
  for (const group of groups) {
    const people = Number(group.attendees), additional = Number(group.guests ?? 0);
    if (!Number.isInteger(people) || people < 0 || !Number.isInteger(additional) || additional < 0) return [];
    countedAttendees += people;
    countedGuests += additional;
    const rank = ranks.get(String(group.classId || '').toLowerCase());
    const isSeparateYear = rank && rank < 6;
    const key = isSeparateYear ? String(rank) : '6+';
    const label = isSeparateYear ? `שיעור ${numberToGimatria(rank)}` : 'שיעור ו+';
    const entry = totals.get(key) || { key, label, count: 0, order: isSeparateYear ? rank : 6 };
    entry.count += people + additional;
    totals.set(key, entry);
  }
  // Do not show a partial or inflated breakdown if links or concurrent answers differ.
  if (countedAttendees !== attendees || countedGuests !== guests) return [];
  return [...totals.values()].filter(entry => entry.count > 0).sort((a, b) => a.order - b.order);
}
